import { Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getListActivityAssigneesQueryKey,
  useAddActivityAssignee
} from '@/api/generated/activities/activities';
import {
  getListTeamsQueryKey,
  listTeams,
  useListDepartments
} from '@/api/generated/departments-teams/departments-teams';
import { useListUsers } from '@/api/generated/users/users';
import { FormModal, ModalFooter, MutationButton, TextField } from '@/components/common';

interface ActivityAssigneePickerModalProps {
  opened: boolean;
  onClose: () => void;
  activityId: string;
  existingTeamIds: string[];
  existingUserIds: string[];
}

interface PickerRow {
  key: string;
  kind: 'team' | 'user';
  id: string;
  name: string;
}

// Isti obrazac kao WorkPackageAssigneePickerModal — namjerno zasebna komponenta, ne
// zajednička apstrakcija (vidi item-picker presedan u CLAUDE.md).
export const ActivityAssigneePickerModal = ({
  opened,
  onClose,
  activityId,
  existingTeamIds,
  existingUserIds
}: ActivityAssigneePickerModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  // Nema flat "list all teams" endpointa — isti obrazac agregacije kao
  // WorkPackageAssigneePickerModal: dohvat odjela pa batch poziva timova po odjelu.
  const { data: departmentsData } = useListDepartments();
  const departments = departmentsData ?? [];
  const teamQueries = useQueries({
    queries: departments.map((department) => ({
      queryKey: getListTeamsQueryKey(department.id),
      queryFn: () => listTeams(department.id ?? ''),
      enabled: opened && !!department.id
    }))
  });
  const allTeams = useMemo(() => teamQueries.flatMap((query) => query.data ?? []), [teamQueries]);

  const { data: usersData } = useListUsers();

  const existingTeamIdSet = new Set(existingTeamIds);
  const existingUserIdSet = new Set(existingUserIds);

  // Deaktivirani tim nestaje iz ponude za NOVE dodjele (WP-05) — ne i iz već postojećih.
  const rows: PickerRow[] = useMemo(() => {
    const teamRows: PickerRow[] = allTeams
      .filter((team) => team.id && team.isActive && !existingTeamIdSet.has(team.id))
      .map((team) => ({
        key: `team:${team.id}`,
        kind: 'team',
        id: team.id as string,
        name: team.name ?? ''
      }));
    const userRows: PickerRow[] = (usersData ?? [])
      .filter((user) => user.id && !existingUserIdSet.has(user.id))
      .map((user) => ({
        key: `user:${user.id}`,
        kind: 'user',
        id: user.id as string,
        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
      }));
    return [...teamRows, ...userRows];
  }, [allTeams, usersData, existingTeamIds, existingUserIds]);

  const query = search.trim().toLowerCase();
  const filteredRows = rows.filter((row) => !query || row.name.toLowerCase().includes(query));

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setSelected(new Set());
  };

  const addMutation = useAddActivityAssignee();

  const handleAdd = async () => {
    setIsAdding(true);
    try {
      for (const row of rows.filter((r) => selected.has(r.key))) {
        await addMutation.mutateAsync({
          id: activityId,
          data: row.kind === 'team' ? { teamId: row.id } : { userId: row.id }
        });
      }
      queryClient.invalidateQueries({ queryKey: getListActivityAssigneesQueryKey(activityId) });
      reset();
      onClose();
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={560}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('activityAssigneePicker.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <TextField
          label={t('activityAssigneePicker.searchLabel')}
          placeholder={t('activityAssigneePicker.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <Stack gap={0} style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 14 }}>
          <Group
            gap={8}
            wrap="nowrap"
            py={4}
            style={{ borderBottom: '1px solid var(--color-border-soft)' }}
          >
            <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
              {t('activityAssigneePicker.colName')}
            </Text>
            <Text fz={11} fw={600} w={70} ta="right" style={{ color: 'var(--color-text-muted)' }}>
              {t('activityAssigneePicker.colType')}
            </Text>
          </Group>

          {filteredRows.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('activityAssigneePicker.emptyState')}
            </Text>
          )}

          {filteredRows.map((row, index) => (
            <Group
              key={row.key}
              gap={8}
              wrap="nowrap"
              py={10}
              role="button"
              tabIndex={0}
              onClick={() => toggle(row.key)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggle(row.key);
              }}
              style={{
                cursor: 'pointer',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Checkbox
                checked={selected.has(row.key)}
                onChange={() => toggle(row.key)}
                onClick={(event) => event.stopPropagation()}
              />
              <Text fz={13} fw={600} style={{ flex: 1, color: 'var(--color-text-primary)' }}>
                {row.name}
              </Text>
              <Text fz={12} w={70} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                {row.kind === 'team'
                  ? t('activityAssigneePicker.typeTeam')
                  : t('activityAssigneePicker.typeIndividual')}
              </Text>
            </Group>
          ))}
        </Stack>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <MutationButton isPending={isAdding} disabled={selected.size === 0} onClick={handleAdd}>
            {t('activityAssigneePicker.addSelected', { count: selected.size })}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
